/**
 * Fortnox OAuth 2.0 client — shared by the web app's OAuth routes and the
 * runtime's token refresh.
 *
 * One Squidler-owned OAuth app serves the whole deployment. Credentials come
 * from env:
 *   FORTNOX_OAUTH_CLIENT_ID
 *   FORTNOX_OAUTH_CLIENT_SECRET
 *   FORTNOX_OAUTH_REDIRECT_URI
 *
 * Two things make Fortnox different from every other provider we integrate,
 * and both are load-bearing:
 *
 *  1. REFRESH TOKENS ROTATE AND ARE SINGLE-USE. Every refresh returns a NEW
 *     refresh token and instantly kills the old one. Two concurrent refreshes
 *     on one connection therefore destroy it — recovery means the full browser
 *     flow again. Callers MUST serialize refreshes per connection; the runtime
 *     does this with a per-connection advisory lock (tools/fortnox/tokens.ts).
 *
 *  2. REFRESH TOKENS EXPIRE 45 DAYS AFTER ISSUE, and API calls do NOT extend
 *     them — only a refresh does. Lazy refresh-on-use is therefore not enough:
 *     a company nobody asks about for seven weeks is permanently disconnected.
 *     A scheduled sweeper (runtime `src/oauth-refresh/`, with Fortnox's
 *     descriptor in `tools/fortnox/refreshable.ts`) refreshes ahead of the
 *     cliff. Do not remove it on the theory that refresh-on-use covers it.
 *
 * Connections live in `integration_connections`, one row per Fortnox company.
 */

import { describeErrorBody } from "../error-detail";

const AUTH_URL = "https://apps.fortnox.se/oauth-v1/auth";
const TOKEN_URL = "https://apps.fortnox.se/oauth-v1/token";

/** Refresh tokens live 45 days from issue. */
export const REFRESH_TOKEN_TTL_DAYS = 45;

interface OAuthConfig {
  readonly clientId: string;
  readonly clientSecret: string;
  readonly redirectUri: string;
}

/** Read the env triple, or null when it is not fully set. Both the getter and
 *  the configured-check go through here so the var names appear once. */
const readConfig = (): OAuthConfig | null => {
  const clientId = process.env.FORTNOX_OAUTH_CLIENT_ID;
  const clientSecret = process.env.FORTNOX_OAUTH_CLIENT_SECRET;
  const redirectUri = process.env.FORTNOX_OAUTH_REDIRECT_URI;
  if (!clientId || !clientSecret || !redirectUri) return null;
  return { clientId, clientSecret, redirectUri };
};

const getConfig = (): OAuthConfig => {
  const config = readConfig();
  if (!config) {
    throw new Error(
      "Fortnox OAuth not configured — set FORTNOX_OAUTH_CLIENT_ID, " +
      "FORTNOX_OAUTH_CLIENT_SECRET, and FORTNOX_OAUTH_REDIRECT_URI",
    );
  }
  return config;
};

export const isFortnoxConfigured = (): boolean => readConfig() !== null;

// --- Auth URL ---

interface BuildAuthUrlArgs {
  readonly scopes: readonly string[];
  readonly state: string;
}

/**
 * Build the consent URL. Fortnox does not support PKCE, so CSRF protection
 * rests entirely on `state` — which we persist server-side in `oauth_states`
 * bound to the initiating user and instance, and consume single-use on
 * callback (same discipline as the Google flow).
 *
 * `account_type=service` mints a service account rather than binding the grant
 * to the consultant who happened to click. That is what we want for an
 * unattended agent: a consultant leaving the bureau must not take 40 client
 * connections with them.
 */
export const buildAuthUrl = ({ scopes, state }: BuildAuthUrlArgs): string => {
  const { clientId, redirectUri } = getConfig();
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: scopes.join(" "),
    state,
    access_type: "offline",
    response_type: "code",
    account_type: "service",
  });
  return `${AUTH_URL}?${params.toString()}`;
};

// --- Token exchange ---

export interface FortnoxTokenResponse {
  readonly access_token: string;
  readonly refresh_token: string;
  readonly expires_in: number;
  readonly scope: string;
  readonly token_type: string;
}

export class FortnoxOAuthError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    /** The HTTP status Fortnox answered with; null when the failure is ours
     *  (a 200 we could not use). */
    public readonly status: number | null = null,
    /** An error body that carried no OAuth `error` field, redacted and
     *  clipped by `describeErrorBody` — for the log, never for the agent. A
     *  bare "HTTP 500" says nothing about which side failed or why. */
    public readonly bodyExcerpt: string | null = null,
  ) {
    super(message);
    this.name = "FortnoxOAuthError";
  }
}

/**
 * Fortnox's token endpoint itself is failing (5xx) or refusing to serve right
 * now (429) — a fault on its side that says nothing about the connection.
 *
 * Distinct from every other error because of what it forbids: the refresh
 * grant goes to the SAME endpoint, so falling back to it spends a single-use,
 * rotating credential into the very outage that just failed the mint. If
 * Fortnox rotates the token and then errors, the connection is lost. Seen on
 * prod 2026-09-25: two companies, mint 500 then refresh 500, seconds apart.
 */
export const isTokenServiceFailing = (err: unknown): err is FortnoxOAuthError =>
  err instanceof FortnoxOAuthError && err.status !== null && (err.status >= 500 || err.status === 429);

/**
 * `invalid_grant` means the refresh token is spent, rotated away, or past 45
 * days — the connection needs the browser flow again and no retry will help.
 *
 * ONLY that. `invalid_request` used to be here too, and it must not be: per
 * RFC 6749 it means OUR request was malformed (a parameter Fortnox no longer
 * accepts, say). Treating it as terminal would abandon every still-valid
 * refresh token in the fleet the day Fortnox tightened a parameter, and send
 * every owner back through the browser flow for a bug that was ours. A
 * malformed request is a retryable failure the sweeper keeps reporting until
 * someone fixes the request.
 */
export const isUnrecoverable = (err: unknown): boolean =>
  err instanceof FortnoxOAuthError && err.code === "invalid_grant";

const basicAuth = (clientId: string, clientSecret: string): string =>
  `Basic ${Buffer.from(`${clientId}:${clientSecret}`).toString("base64")}`;

/** The POST and its error shape, shared by every grant type. Returns the
 *  parsed body; each caller validates the fields IT requires. */
const requestToken = async (
  body: URLSearchParams,
  extraHeaders: Readonly<Record<string, string>> = {},
): Promise<Record<string, unknown>> => {
  const { clientId, clientSecret } = getConfig();
  const res = await fetch(TOKEN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: basicAuth(clientId, clientSecret),
      ...extraHeaders,
    },
    body,
  });
  // Read as text first: an error body is often not JSON (a gateway's HTML
  // page, or nothing), and it is exactly then that the body is worth keeping.
  const text = await res.text().catch(() => "");
  let parsed: unknown = {};
  try { parsed = JSON.parse(text); } catch { /* not JSON — kept as `text` */ }
  const payload = (parsed ?? {}) as Record<string, unknown>;
  if (!res.ok) {
    const oauthCode = typeof payload.error === "string" ? payload.error : null;
    const description = typeof payload.error_description === "string"
      ? payload.error_description
      : `HTTP ${res.status}`;
    const excerpt = oauthCode === null ? describeErrorBody(text) ?? null : null;
    throw new FortnoxOAuthError(oauthCode ?? `http_${res.status}`, description, res.status, excerpt);
  }
  return payload;
};

export interface FortnoxServiceToken {
  readonly access_token: string;
  readonly expires_in: number;
  /** What the customer's stored consent actually permits. Authoritative —
   *  prefer it over our own `granted_scopes`, which records what we ASKED for
   *  at authorization time and can drift. Absent on some responses. */
  readonly scope: string | null;
}

/**
 * Mint an access token for one company with `grant_type=client_credentials`,
 * against the consent Fortnox stores for (our client_id, that customer).
 *
 * THIS IS THE WAY OUT OF THE REFRESH TOKEN. A refresh token is single-use,
 * rotates on every use, dies 45 days after issue, and is the only copy of a
 * live credential — the whole reason `tokens.ts` needs an advisory lock and
 * the sweeper exists. Here there is no per-connection credential at all: the
 * inputs are our own client id and secret plus the tenant's own number, so a
 * token can be minted at any time, as often as needed, and nothing can be
 * spent or lost.
 *
 * It also lifts the one-connection-per-company limit. Fortnox allows only ONE
 * service account per (client_id, customer), so a second browser
 * authorization silently invalidated the first agent's refresh token — which
 * is how three connections died unnoticed on prod. Two tokens minted here for
 * the same tenant are both valid at once (verified against the live API,
 * 2026-09-23), so any number of agents can serve one company.
 *
 * The consent is created by the ordinary `account_type=service` browser flow,
 * exists for every company we already hold, and is "valid indefinitely until
 * they actively choose to revoke it" — so this needs nothing from the customer.
 *
 * `tenantId` is the company's Fortnox database number.
 */
export const mintServiceAccountToken = async (tenantId: string): Promise<FortnoxServiceToken> => {
  const payload = await requestToken(
    new URLSearchParams({ grant_type: "client_credentials" }),
    // Numeric, and the header name is exactly this.
    { TenantId: tenantId },
  );
  if (typeof payload.access_token !== "string" || typeof payload.expires_in !== "number") {
    throw new FortnoxOAuthError("malformed_token_response", "client_credentials response missing access_token or expires_in");
  }
  return {
    access_token: payload.access_token,
    expires_in: payload.expires_in,
    scope: typeof payload.scope === "string" ? payload.scope : null,
  };
};

const postToken = async (body: URLSearchParams): Promise<FortnoxTokenResponse> => {
  const payload = await requestToken(body);
  if (typeof payload.access_token !== "string" || typeof payload.refresh_token !== "string") {
    // A 200 without both tokens would otherwise be written over the only live
    // credential we hold. Fail loudly instead — see the staged write in
    // tools/fortnox/tokens.ts.
    throw new FortnoxOAuthError("malformed_token_response", "token response missing access_token or refresh_token");
  }
  return payload as unknown as FortnoxTokenResponse;
};

/** Exchange an authorization code (10-minute TTL, single use) for tokens. */
export const exchangeCode = (code: string): Promise<FortnoxTokenResponse> => {
  const { redirectUri } = getConfig();
  return postToken(new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
  }));
};

/**
 * Redeem a refresh token. The returned `refresh_token` REPLACES the one passed
 * in — the caller must persist it before the next refresh, or the connection
 * is lost. Never call this concurrently for the same connection.
 */
export const refreshAccessToken = (refreshToken: string): Promise<FortnoxTokenResponse> =>
  postToken(new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
  }));
